import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiClipboard, FiCheck, FiMessageCircle, FiUsers, FiBarChart
} from 'react-icons/fi';
import { colors } from '../../../utils/colors';

function ProcurementManagerDashboard({ user, dashboardData }) {
  // Helper function to check if current user is the approver at this stage (including delegates)
  const isUserCurrentApprover = (req) => {
    if (!user || !user.user_id) return false;
    
    const userId = String(user.user_id);
    const reqStatus = req.requisition_status;
    
    console.log(`DEBUG: isUserCurrentApprover for ${req.requisition_number}:`, {
      userId,
      userRole: user.role,
      reqStatus,
      mid: req.mid,
      second: req.second,
      approval: req.approval
    });
    
    // For PM Dashboard: If user has PM role (4), they can see indicators for requisitions 
    // that require PM approval regardless of current workflow stage
    if (user.role === 4 || user.role === '4') {
      // PM can see indicators for all PM approval requisitions
      return true;
    }
    
    // Check if it's the user's turn based on status
    if (reqStatus === 0) {
      // First approval stage
      return String(req.mid) === userId || String(req.mid_delegate?.user_id) === userId;
    } else if (reqStatus === 1) {
      // Second approval stage
      return String(req.second) === userId || String(req.second_delegate?.user_id) === userId;
    } else if (reqStatus === 2 && req.specification_approvals >= 3) {
      // Third approval stage (only if required)
      // Check if quote exceeds site limit to determine approver type
      const quotationTotal = parseFloat(req.quotation_total?.toString().replace(/[^\d.-]/g, '') || 0);
      const siteLimit = 20000; // Default site limit
      
      if (quotationTotal > siteLimit) {
        // High value: SAP approver
        return String(req.approval) === userId || String(req.sap_delegate?.user_id) === userId;
      } else {
        // Standard value: PM role (role 4)
        return user.role === 4 || user.role === '4';
      }
    }
    
    return false;
  };

  // Helper function to check if approver is waiting for more information from requester
  const isApproverWaitingForInfo = (req) => {
    if (!user || !user.user_id) return false;
    
    // Must be the current approver for this to matter
    if (!isUserCurrentApprover(req)) return false;
    
    // Check if there's a last comment by this approver
    const userId = String(user.user_id);
    const lastCommentUser = req.last_comment_user ? String(req.last_comment_user) : null;
    const lastCommentApproved = req.last_comment_approved;
    const lastCommentText = req.last_comment_text || '';
    
    // Debug logging
    console.log(`DEBUG: Req ${req.requisition_number}:`, {
      userId,
      lastCommentUser,
      lastCommentApproved,
      isCurrentApprover: isUserCurrentApprover(req),
      userRole: user.role,
      reqStatus: req.requisition_status
    });
    
    // Return true if:
    // 1. Last comment was made by current user (approver)
    // 2. Last comment was NOT an approval (comment_approved = 0)
    // 3. Last comment was NOT a rejection (doesn't contain rejection keywords)
    if (lastCommentUser === userId && 
        lastCommentApproved === 0 && 
        !lastCommentText.toLowerCase().includes('rejection reason:') &&
        !lastCommentText.toLowerCase().includes('rejected')) {
      console.log(`DEBUG: Req ${req.requisition_number} should show yellow indicator`);
      return true;
    }
    
    return false;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div style={{background: 'var(--gradient-primary)'}} className="rounded-xl p-4 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          {/* Title Section */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-1 text-white">Procurement Manager Dashboard</h1>
            <p className="text-white opacity-90 text-sm">Manage requisitions and procurement processes efficiently</p>
          </div>

           {/* User Info */}
          <div className="hidden xl:block">
            <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm min-w-[160px] h-[68px] flex flex-col justify-center">
              <div className="text-white text-xs opacity-75 leading-tight">Welcome back,</div>
              <div className="text-white font-semibold leading-tight mt-0.5">{user?.user_full_name}</div>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/dashboard/requisitions?status=awaiting_my_pm_approval"
              className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 hover:bg-opacity-20 transition-all duration-200 border border-white border-opacity-20 min-w-[220px] h-[68px] flex items-center"
            >
              <div className="flex items-center gap-3 w-full">
                <div 
                  className="rounded-full p-2.5 flex items-center justify-center"
                  style={{ backgroundColor: colors.pictonBlue }}
                >
                  <FiClipboard className="text-white text-xl" />
                </div>
                <div className="flex-1">
                  <div className="text-white text-xs opacity-75 leading-tight">You have</div>
                  <div className="text-white font-bold leading-tight mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-2xl">{dashboardData?.pending_pm_approval_count || 0}</span>
                    <span className="text-sm">Pending PM Approvals</span>
                  </div>
                </div>
              </div>
            </Link>
            
            <Link 
              to="/dashboard/requisitions?status=awaiting_my_approvals"
              className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 hover:bg-opacity-20 transition-all duration-200 border border-white border-opacity-20 min-w-[220px] h-[68px] flex items-center"
            >
              <div className="flex items-center gap-3 w-full">
                <div 
                  className="rounded-full p-2.5 flex items-center justify-center"
                  style={{ backgroundColor: colors.pictonBlue }}
                >
                  <FiCheck className="text-white text-xl" />
                </div>
                <div className="flex-1">
                  <div className="text-white text-xs opacity-75 leading-tight">You have</div>
                  <div className="text-white font-bold leading-tight mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-2xl">{dashboardData?.pending_gl_approval_count || 0}</span>
                    <span className="text-sm">Pending GL Approvals</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
          
         
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Pending Assignment */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-blue-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#e3f2fd'}}>
                  <FiClipboard className="w-3.5 h-3.5" style={{color: '#0086D4'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Pending Assignment
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">
                    {dashboardData.pending_assignment ? dashboardData.pending_assignment.length : 0} pending
                  </p>
                </div>
              </div>
              <Link
                to="/dashboard/requisitions?status=approved"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>
          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {!dashboardData.pending_assignment || dashboardData.pending_assignment.length === 0 ? (
              <div className="p-8 text-center">
                <FiClipboard className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-primary)', opacity: 0.3}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No pending assignments</p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="font-semibold px-3 py-2 text-left">Number</th>
                      <th className="font-semibold px-3 py-2 text-left">Department</th>
                      <th className="font-semibold px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.pending_assignment.map((req, index) => (
                      <tr
                        key={index}
                        className="border-b transition hover:bg-blue-50"
                      >
                        <td className="px-3 py-2 font-bold text-primary">
                          EPR-{req.requisition_number}
                        </td>
                        <td className="px-3 py-2">
                          {req.department_name}
                        </td>
                        <td className="px-3 py-2">
                          <Link 
                            to={`/dashboard/requisitions/${req.requisition_id}`} 
                            className="px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity inline-block" 
                            style={{backgroundColor: 'var(--color-primary)', color: 'white'}}
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Requisitions Pending PM Approval */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-amber-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#fff3cd'}}>
                  <FiCheck className="w-3.5 h-3.5" style={{color: '#ffc107'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Requisitions Pending PM Approval
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">
                    {dashboardData.pending_pm_approval_count || 0} pending
                  </p>
                </div>
              </div>
              <Link
                to="/dashboard/requisitions?status=awaiting_my_pm_approval"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>
          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {!dashboardData.pending_pm_approval || dashboardData.pending_pm_approval.length === 0 ? (
              <div className="p-8 text-center">
                <FiCheck className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-success)', opacity: 0.3}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No requisitions pending PM approval</p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="font-semibold px-3 py-2 text-left">Number</th>
                      <th className="font-semibold px-3 py-2 text-left">Project</th>
                      <th className="font-semibold px-3 py-2 text-left">Department</th>
                      <th className="font-semibold px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.pending_pm_approval.map((req, index) => (
                      <tr
                        key={index}
                        className={`border-b transition hover:bg-amber-50 ${
                          isApproverWaitingForInfo(req) 
                            ? 'bg-yellow-100 border-l-4 border-l-yellow-500' 
                            : ''
                        }`}
                      >
                        <td className="px-3 py-2 font-bold text-primary">
                          EPR-{req.requisition_number}
                        </td>
                        <td className="px-3 py-2">
                          {req.specification_title || 'Untitled Project'}
                        </td>
                        <td className="px-3 py-2">
                          {req.department_name}
                        </td>
                        <td className="px-3 py-2">
                          <Link 
                            to={`/dashboard/requisitions/${req.requisition_id}`} 
                            className={`px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity inline-block text-white ${
                              isApproverWaitingForInfo(req) 
                                ? 'bg-yellow-600' 
                                : 'bg-gray-600'
                            }`}
                          >
                            {isApproverWaitingForInfo(req) ? 'Awaiting Response' : 'Review'}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

   
    </div>
  );
}

export default ProcurementManagerDashboard;
